import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ChevronLeft, Trophy, Lock, Check } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { toast } from "sonner";

export default function Memorial() {
  const { state, refreshState } = useAuth();
  const [logs, setLogs] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [titles, setTitles] = useState([]);
  const [activeTitle, setActiveTitle] = useState(null);
  const [tab, setTab] = useState("achievements");
  const navigate = useNavigate();

  const load = async () => {
    const [l, a] = await Promise.all([
      api.get("/battle/log"),
      api.get("/achievements/list"),
    ]);
    setLogs(l.data.logs || []);
    setAchievements(a.data.items || []);
    setTitles(a.data.available_titles || []);
    setActiveTitle(a.data.active_title);
    if (a.data.newly_earned_keys?.length) {
      a.data.newly_earned_keys.forEach((k) => {
        const ach = (a.data.items || []).find((it) => it.key === k);
        if (ach) toast.success(`Achievement: ${ach.name}`);
      });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setTitle = async (t) => {
    try {
      await api.post("/achievements/title", { title: t });
      setActiveTitle(t);
      refreshState();
      toast.success(t ? `Title set: ${t}` : "Title cleared.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    }
  };

  if (!state) return null;

  return (
    <div className="min-h-screen">
      <NavBar active="memorial" />

      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-16">
        <button onClick={() => navigate("/command")} className="btn-ghost mb-6 flex items-center gap-1"
                data-testid="memorial-back-button">
          <ChevronLeft className="w-3 h-3" /> COMMAND
        </button>

        <p className="tac-label fade-up">MEMORIAL // FALLEN HORDE & DEEDS</p>
        <h1 className="font-serif-h text-5xl lg:text-7xl uppercase text-bone leading-none mt-3 fade-up"
            data-testid="memorial-title">
          They will not<br />rise again.
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-12 fade-up delay-1">
          <Big label="FALLEN" value={state.fallen_zombies} testid="memorial-fallen" />
          <Big label="CONVERTED" value={state.humans_converted} testid="memorial-converted" />
          <Big label="HUMANS KILLED" value={state.total_kills} testid="memorial-kills" />
          <Big label="BATTLES" value={`${state.battles_won} / ${state.battles_won + state.battles_lost}`} testid="memorial-battles" />
        </div>

        {/* Tabs */}
        <div className="mt-12 flex gap-1 border-b border-subtle">
          <TabBtn active={tab==="achievements"} onClick={() => setTab("achievements")} testid="tab-achievements">
            <Trophy className="w-3 h-3" /> ACHIEVEMENTS
          </TabBtn>
          <TabBtn active={tab==="titles"} onClick={() => setTab("titles")} testid="tab-titles">
            TITLES
          </TabBtn>
          <TabBtn active={tab==="log"} onClick={() => setTab("log")} testid="tab-log">
            BATTLE LOG
          </TabBtn>
        </div>

        {/* Achievements */}
        {tab==="achievements" && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 fade-up">
            {achievements.map((a) => (
              <div key={a.key}
                   className={`tactical-card transition-all ${a.earned ? "border-blood" : "opacity-60"}`}
                   data-testid={`achievement-${a.key}`}>
                <div className="flex items-start gap-2">
                  {a.earned ? <Check className="w-4 h-4 text-blood mt-0.5" /> : <Lock className="w-4 h-4 text-muted-h mt-0.5" />}
                  <div>
                    <p className="font-mono text-bone text-sm">{a.name}</p>
                    <p className="text-[0.7rem] text-muted-h mt-1 leading-relaxed">{a.desc}</p>
                    {a.earned && a.title && (
                      <p className="tac-label mt-2 text-blood">UNLOCKS TITLE: {a.title}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Titles */}
        {tab==="titles" && (
          <div className="mt-8 fade-up">
            {titles.length === 0 ? (
              <p className="text-sm text-muted-h" data-testid="titles-empty">
                NO TITLES YET. EARN ACHIEVEMENTS THAT GRANT THEM.
              </p>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => setTitle(null)}
                  className={`tactical-card text-left w-full p-4 ${activeTitle===null?"border-blood":""}`}
                  data-testid="title-clear">
                  <p className="font-mono text-bone">— No Title —</p>
                  <p className="text-[0.7rem] text-muted-h mt-1">Display only your callsign.</p>
                </button>
                {titles.map((t) => (
                  <button key={t}
                          onClick={() => setTitle(t)}
                          className={`tactical-card text-left w-full p-4 ${activeTitle===t?"border-blood":""}`}
                          data-testid={`title-${t.replace(/\s+/g,'-').toLowerCase()}`}>
                    <p className="font-mono text-bone">{t}</p>
                    {activeTitle===t && <p className="tac-label mt-1">ACTIVE</p>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Battle log */}
        {tab==="log" && (
          <div className="mt-8 narrative-panel relative fade-up">
            <div className="absolute inset-0 opacity-15 pointer-events-none">
              <img src={ASSETS.horde} alt="" className="w-full h-full object-cover mix-blend-overlay" />
            </div>
            <div className="space-y-6 relative max-h-[60vh] overflow-y-auto pr-2">
              {logs.length === 0 && (
                <p className="text-sm text-muted-h" data-testid="memorial-empty-log">
                  NO BATTLES YET. THE HORDE STILL DREAMS.
                </p>
              )}
              {logs.map((log) => (
                <div key={log.id} className="border-l-2 pl-4"
                     style={{ borderColor: log.won ? "#1B4D3E" : "#4A0000" }}
                     data-testid={`log-entry-${log.id}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <p className="font-serif-h text-xl text-bone uppercase">{log.battle}</p>
                    <span className="tac-label" style={{ color: log.won ? "#7FBA9D" : "#FF3B30" }}>
                      {log.won ? "VICTORY" : "DEFEAT"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-h tracking-widest mb-2">
                    {log.region} · {log.mode?.toUpperCase()} · COMMITTED {log.committed} · FELL {log.zombie_casualties} · CONVERTED {log.converted}
                  </p>
                  <p className="text-sm text-[var(--text-main)] leading-relaxed whitespace-pre-line">
                    {log.narrative}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Big = ({ label, value, testid }) => (
  <div className="tactical-card">
    <p className="tac-label">{label}</p>
    <p className="font-serif-h text-3xl text-bone mt-1" data-testid={testid}>{value}</p>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick}
          className={`px-4 py-3 tac-label flex items-center gap-2 transition-colors
                      ${active ? "text-bone border-b border-blood -mb-px" : "text-muted-h hover:text-bone"}`}
          data-testid={testid}>
    {children}
  </button>
);
